import React, { FunctionComponent, useState } from 'react'
import DropMenu from 'blocks.simple/dropMenu/dropMenu'
import Button from 'blocks.simple/button/button'
import config from 'core/config'
import styles from './ActionBar-styles'
import grid from 'blocks.simple/grid/grid.jcss'
import { text as textStyles } from 'theme'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { isExist } from 'core/utils/coreUtil'
import Icon from 'blocks.simple/icon/icon'
import { IActionBarComponent } from './ActionBar-types'
import { useActionBar, useUploadDelimeter } from './ActionBar-hooks'
import {
    ActionBarRequestHeaderType,
    ActionBarAction,
    ActionBarActionType,
    ActionBarButtonAction,
    ActionBarDateIntervalAction,
} from 'core/models/ActionBar'
import { constants } from 'core/constants'
import { useDispatch } from 'react-redux'
import { IUserState } from 'blocks.app/user/user.state'
import { useTypedSelector } from 'core/store/hooks'
import { DateInterval } from 'molecules/DateInterval/DateInterval'
import { isHorizontalMenu } from 'core/helpers/menu'
import Uploader from 'blocks.app/uploader'
import { ApiPostEventsType } from 'core/api/events'
import ModalWithBackground from 'blocks.simple/modalWithBackground/modalWithBackground'

export const CatalogActionBar: FunctionComponent<IActionBarComponent> = ({
    actions,
    selectedItems,
    fakeLine,
    requestHeaderType,
    type,
    title,
}) => {
    const dispatch = useDispatch()
    const DEFAULT_UPLOAD_URL: string = `${config.api.root}v2/upload/school/instances_catalog`
    const { activeElements, setActiveElements } = useActionBar()
    const { delimiter, setDelimiter } = useUploadDelimeter(constants.FILE_UPLOAD_CSV_DELIMITER)
    const { tokens } = useTypedSelector<IUserState>((state) => state.user)
    const [openUploadModal, setOpenUploadModal] = useState(false)

    const getHeadersByType = () => {
        switch (requestHeaderType) {
            case ActionBarRequestHeaderType.WITH_AUTH:
                return tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {}
            default:
                return {}
        }
    }

    const sortActionByOrder = (actions: ActionBarAction[]) =>
        [...actions].sort((first, second) => first.order - second.order)

    const deleteItem = (action: ActionBarButtonAction) => {
        return {
            id: 'delete',
            icon: 'delete',
            disabled: selectedItems.length === 0 && !fakeLine,
            color: 'grey',
            tooltip: {
                title: translate('delete'),
            },
            children: (
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        setActiveElements([])
                        action.cb()
                    }}
                    className={cn(grid.colCenter, styles.allertModalForm)}
                >
                    <div className={cn(styles.allertModalTitle, textStyles.center)}>{translate('deleteApprove')}</div>

                    <div className={cn(grid.row, grid.justify)}>
                        <Button
                            type="submit"
                            mod="fill"
                            indentSize={'mini'}
                            className={cn(styles.allertModalButton)}
                            textSize={'mini'}
                            rounded={'full_normal'}
                        >
                            {translate('yes')}
                        </Button>
                        <Button
                            mod={'withBorder'}
                            animation={false}
                            onClick={() => setActiveElements([])}
                            indentSize={'mini'}
                            textSize={'mini'}
                            rounded={'full_normal'}
                        >
                            {translate('no')}
                        </Button>
                    </div>
                </form>
            ),
        }
    }

    const handleAddButtonClick = (action: ActionBarButtonAction) => {
        action.cb()
    }

    const dateInterval = (action: ActionBarDateIntervalAction) => {
        return (
            <div className={cn(styles.dateInterval)}>
                <DateInterval onChange={action.cb} dateBegin={action.dateBegin} dateEnd={action.dateEnd} />
            </div>
        )
    }

    const getAddActionComponent = (action: ActionBarButtonAction) => {
        return (
            <div className={cn(styles.addButton)} key={action.type}>
                <Icon
                    onClick={() => handleAddButtonClick(action)}
                    type={'plus_circle'}
                    tooltip={{ title: translate('add') }}
                />
            </div>
        )
    }

    const getDeleteActionComponent = (action: ActionBarButtonAction) => {
        return (
            <div className={cn(styles.deleteButton)} key={action.type}>
                <DropMenu
                    active={activeElements}
                    onChange={setActiveElements}
                    items={[deleteItem(action)]}
                    gray={true}
                />
            </div>
        )
    }

    const getUploadActionComponent = (action: ActionBarButtonAction) => {
        const options = action.options

        return (
            <div className={cn(styles.uploadButton)} key={action.type}>
                <ModalWithBackground isOpen={openUploadModal} onClose={() => setOpenUploadModal(false)}>
                    <Uploader
                        uploadMethod={
                            (options?.upload
                                ? `${config.api.root}${options.upload}`
                                : DEFAULT_UPLOAD_URL) as ApiPostEventsType
                        }
                        isModal={true}
                        onClose={() => setOpenUploadModal(false)}
                    />
                </ModalWithBackground>
                <Button
                    mod={'withBorder'}
                    iconPos="left"
                    icon="upload_arrow"
                    animation={false}
                    onClick={() => setOpenUploadModal(true)}
                >
                    {translate('upload')}
                </Button>
            </div>
        )
    }

    const renderActionComponents = () => {
        if (!isExist(actions) || actions.length === 0) return null

        return sortActionByOrder(actions).map((action) => {
            switch (action.type) {
                case ActionBarActionType.ADD:
                    return getAddActionComponent(action)
                case ActionBarActionType.DELETE:
                    return getDeleteActionComponent(action)
                case ActionBarActionType.UPLOAD:
                    return getUploadActionComponent(action)
                case ActionBarActionType.DATE_INTERVAL:
                    return dateInterval(action)
                default:
                    return null
            }
        })
    }
    const horizontalMenu = isHorizontalMenu()
    return (
        <div
            className={cn(
                styles.actionBarWrapper,
                horizontalMenu && type === 'survey' && styles.actionBarWrapper_x,
                horizontalMenu && type === 'quiz' && styles.actionBarWrapper_x,
                horizontalMenu && type === 'lightOfHistory' && styles.actionBarWrapper_x,
                horizontalMenu && type === 'lensOfTime' && styles.actionBarWrapper_x
            )}
        >
            <div className={styles.content}>
                <div
                    className={cn(
                        grid.startJustify,
                        styles.tools,
                        horizontalMenu && styles.margin_x,
                        horizontalMenu && type === 'exhibitions' && styles.margin_no_x,
                        horizontalMenu && type === 'authors' && styles.margin_no_x
                    )}
                >
                    {renderActionComponents()}
                </div>
                <div className={styles.title}>{title}</div>
            </div>
        </div>
    )
}
